/**
 * CANONICAL MODULE — app-platform/shared/authGate.ts
 *
 * This file is copied byte-for-byte into every app (`src/lib/authGate.ts`). It imports nothing from
 * any app, renders nothing, and touches no browser API: it decides *which* screen belongs on screen
 * and leaves the app to render its own.
 *
 * **Fix bugs HERE, then copy outward. Never edit an app's copy in place.**
 *
 * ── Why this is a function and not a component ───────────────────────────────────────────────────
 * The W9 audit asked for one shared `AuthGate` component. That was rejected, for the same reason
 * Phase 3 does not flatten the five palettes: the five gates are not five stabs at one screen. One
 * is a three-way offline machine with a captive-portal escape hatch; one lives inside a five-phase
 * sync context; one has a synchronous pre-check whose only job is to kill a one-frame flash; one has
 * no loading state at all. Each renders that app's own locked visual identity.
 *
 * What *is* identical in all five is the reasoning underneath — and that is where every bug in this
 * system has actually been. So the decision is shared and the screens are not. This is the
 * `healthChecks` shape (a shared factory plus each app's own checks), not the `errorLog` shape.
 *
 * ── Where this module stops ──────────────────────────────────────────────────────────────────────
 * It ends at "the app may render". The post-auth phases — first-sync, sync-failed, reconcile — are
 * **sync** states wearing a gate's clothes. They differ legitimately per app and they belong to W10
 * and W11. Do not add them here.
 */

export type AuthView =
  /** MSAL has not settled yet. Render a neutral ground, not a sign-in screen — see `hasAccount`. */
  | { kind: 'initialising' }
  /** A brand-new signed-out visitor. Only for apps with a landing page. */
  | { kind: 'landing' }
  /** A signed-out visitor who has been here before, or an app with no landing page. */
  | { kind: 'sign-in'; canWorkOffline: boolean }
  /** An automatic sign-in redirect is in flight; the page is about to navigate away. */
  | { kind: 'signing-in' }
  /** Render the app. Includes the trusted-device offline path, where there is no account at all. */
  | { kind: 'app' }

export interface GateInput {
  /** MSAL has finished initialising and handled any redirect response. */
  ready: boolean
  /**
   * An account is present.
   *
   * Compute this **synchronously and generously** — `isAuthenticated || getActiveAccount() != null
   * || getAllAccounts().length > 0`. A narrower check shows an already-signed-in user a one-frame
   * flash of the sign-in screen before the MSAL context settles.
   */
  hasAccount: boolean
  /** An automatic sign-in redirect has been started and has not resolved. */
  autoLoginInFlight: boolean
  /** `navigator.onLine`. See `trustedDevice` for why this is not trusted on its own. */
  online: boolean
  /**
   * This device has synced before and holds usable local data.
   *
   * **Read this regardless of `navigator.onLine`.** A captive portal reports `onLine === true` on a
   * network that reaches nothing, and that exact case was a shipped bug: the gate believed it was
   * online, waited for Microsoft, and stranded a writer with local work they could not reach.
   */
  trustedDevice: boolean
  /** The user pressed "keep working offline" on the sign-in screen. */
  enteredOffline: boolean
  /** This device has completed a sign-in before. Gates landing page vs sign-in screen. */
  returningVisitor: boolean
  /** Does this app have a landing page at all? `false` folds `landing` into `sign-in`. */
  hasLandingPage: boolean
  /**
   * The cold-start backstop has elapsed.
   *
   * **The timeout fails open**: on expiry the user enters the app with whatever is cached, rather
   * than meeting an error screen. The rule is quil's and it is now estate-wide — *the cold-start
   * barrier can never strand the user*. The duration is per-app config; the direction is not.
   */
  timedOut: boolean
}

/**
 * Which screen belongs on screen right now.
 *
 * Ordering matters here and is load-bearing:
 *
 * - `hasAccount` is checked before `ready`, so a returning user never sees a flash of sign-in.
 * - `timedOut` beats `ready`, because the backstop exists precisely for the case where readiness
 *   never arrives.
 * - The offline branches beat `signing-in`, because a trusted device with local work should get
 *   that work rather than a spinner pointed at a network it cannot reach.
 */
export function decideAuthView(input: GateInput): AuthView {
  // Signed in, or the backstop gave up waiting: the app renders with whatever is cached.
  if (input.hasAccount) return { kind: 'app' }
  if (input.timedOut && input.trustedDevice) return { kind: 'app' }

  // A trusted device that is offline, or has chosen offline, works locally with no account at all.
  // Sync stays paused because the token getter has no account to use, which is the correct state.
  if (input.trustedDevice && (!input.online || input.enteredOffline)) return { kind: 'app' }

  if (!input.ready) return { kind: 'initialising' }
  if (input.autoLoginInFlight) return { kind: 'signing-in' }

  // Signed out and settled. A brand-new visitor meets the app's own front door, never Microsoft's.
  if (!input.returningVisitor && input.hasLandingPage) return { kind: 'landing' }

  // "Keep working offline" is offered only to a device that actually has something to work on.
  return { kind: 'sign-in', canWorkOffline: input.trustedDevice }
}
